// Lapisan data BUNDEL (mig 8040) — pembaca storefront, RPC admin, dan
// pembelian bundel milik pembeli. Rumus harga TIDAK di sini: `src/lib/bundle.ts`.

import { createBrowserSupabase } from "@/lib/supabase/browser";
import { getClientStoreId } from "@/lib/supabase/storeContextClient";
import { BUNDLE_SELECT, mapBundleRow, type Bundle } from "@/lib/bundle";

// ─── Storefront (anon / pembeli) ─────────────────────────────────────────────

/**
 * Bundel AKTIF toko pada Host ini. Periode & ketersediaan komponen dinilai di
 * app (`isBundleRunning`), idiom promo. FAIL-CLOSED terhadap toko (aturan A2):
 * toko tak ter-resolusi = daftar kosong, tak pernah query tanpa filter.
 */
export async function fetchActiveBundles(): Promise<Bundle[]> {
  const db = createBrowserSupabase("store");
  if (!db) return [];
  const storeId = getClientStoreId();
  if (!storeId) return [];
  const { data, error } = await db
    .from("bundles")
    .select(BUNDLE_SELECT)
    .eq("is_active", true)
    .eq("store_id", storeId);
  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data as any[]) ?? []).map(mapBundleRow);
}

/**
 * Definisi bundel untuk CHECKOUT — dibaca ulang dari DB, STRICT: galat
 * melempar (checkout tak boleh menebak harga bundel dari keranjang).
 */
export async function fetchBundlesForCheckout(ids: readonly string[]): Promise<Bundle[]> {
  if (!ids.length) return [];
  const db = createBrowserSupabase("store");
  if (!db) throw new Error("Supabase belum siap");
  const storeId = getClientStoreId();
  if (!storeId) throw new Error("Toko tidak dikenali — muat ulang halaman.");
  const { data, error } = await db
    .from("bundles")
    .select(BUNDLE_SELECT)
    .in("id", [...new Set(ids)])
    .eq("store_id", storeId);
  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data as any[]) ?? []).map(mapBundleRow);
}

// ─── Pembelian bundel milik pembeli ─────────────────────────────────────────

export type OrderBundleSummary = {
  id: string;
  bundleId: string | null;
  bundleName: string;
  productType: "preorder" | "ready_stock";
  qty: number;
  /** Per 1 bundel, tanpa voucher & tanpa tambahan. */
  bundlePrice: number;
  separatePrice: number;
  extrasTotal: number;
  createdAt: string | null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapOrderBundleRow(row: any): OrderBundleSummary {
  return {
    id: String(row.id),
    bundleId: row.bundle_id ? String(row.bundle_id) : null,
    bundleName: String(row.bundle_name ?? "Bundling"),
    productType: row.product_type === "preorder" ? "preorder" : "ready_stock",
    qty: Number(row.qty) || 0,
    bundlePrice: Number(row.bundle_price) || 0,
    separatePrice: Number(row.separate_price) || 0,
    extrasTotal: Number(row.extras_total) || 0,
    createdAt: row.created_at ?? null
  };
}

const ORDER_BUNDLE_SELECT =
  "id, bundle_id, bundle_name, product_type, qty, bundle_price, separate_price, extras_total, created_at";

/**
 * Ringkasan pembelian bundel untuk label baris pesanan. Sesi pembeli membaca
 * MILIKNYA (RLS "own read"); sesi admin membaca tokonya ("admin read").
 */
export async function fetchOrderBundles(
  ids: readonly string[],
  scope?: "admin"
): Promise<Map<string, OrderBundleSummary>> {
  const out = new Map<string, OrderBundleSummary>();
  const unique = [...new Set(ids.filter(Boolean))];
  if (!unique.length) return out;
  const db = scope === "admin" ? createBrowserSupabase("admin") : createBrowserSupabase();
  if (!db) return out;
  for (let i = 0; i < unique.length; i += 200) {
    const { data, error } = await db
      .from("order_bundles")
      .select(ORDER_BUNDLE_SELECT)
      .in("id", unique.slice(i, i + 200));
    if (error) throw new Error(error.message);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const row of (data as any[]) ?? []) {
      const m = mapOrderBundleRow(row);
      out.set(m.id, m);
    }
  }
  return out;
}

/** Pembeli membatalkan bundel Pre-Order UTUH (PO OPEN, belum ternota/terbayar). */
export async function cancelMyOpenPoBundle(orderBundleId: string): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap");
  const { error } = await db.rpc("cancel_my_open_po_bundle", { p_order_bundle_id: orderBundleId });
  if (error) throw new Error(error.message);
}

// ─── Admin ───────────────────────────────────────────────────────────────────

/**
 * Semua bundel toko (aktif & tidak). RLS admin memakai cabang escape
 * `current_store_id() is null or …` (audit A6 promo) — disaring ke toko Host.
 */
export async function fetchBundlesAdmin(): Promise<Bundle[]> {
  const db = createBrowserSupabase("admin");
  if (!db) return [];
  const { data, error } = await db
    .from("bundles")
    .select(BUNDLE_SELECT)
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows = ((data as any[]) ?? []).map(mapBundleRow);
  // Sesi ADMIN sudah di-scope RLS ke tokonya; saringan Host menutup pemilik
  // platform tanpa toko di profil (idiom fetchActivePromoCampaignsAdmin).
  const hostStore = getClientStoreId();
  return hostStore ? rows.filter((b) => b.storeId === hostStore) : rows;
}

export async function fetchBundleAdmin(id: string): Promise<Bundle | null> {
  const db = createBrowserSupabase("admin");
  if (!db) return null;
  const { data, error } = await db.from("bundles").select(BUNDLE_SELECT).eq("id", id).maybeSingle();
  if (error) throw new Error(error.message);
  return data ? mapBundleRow(data) : null;
}

/** Simpan (buat / ubah) — RPC ber-izin `produk.bundel`, sinkron komponen ber-diff. */
export async function saveBundle(payload: Record<string, unknown>): Promise<string> {
  const db = createBrowserSupabase("admin");
  if (!db) throw new Error("Supabase belum siap");
  const { data, error } = await db.rpc("admin_save_bundle", { p_bundle: payload });
  if (error) throw new Error(error.message);
  return String(data);
}

export async function setBundleActive(id: string, isActive: boolean): Promise<void> {
  const db = createBrowserSupabase("admin");
  if (!db) throw new Error("Supabase belum siap");
  const { error } = await db.rpc("admin_set_bundle_status", { p_bundle_id: id, p_active: isActive });
  if (error) throw new Error(error.message);
}

export async function deleteBundle(id: string): Promise<void> {
  const db = createBrowserSupabase("admin");
  if (!db) throw new Error("Supabase belum siap");
  const { error } = await db.rpc("admin_delete_bundle", { p_bundle_id: id });
  if (error) throw new Error(error.message);
}

/** Jumlah dipesan per bundel (Σ qty pembelian yang tak batal). */
export async function fetchBundlesOverview(): Promise<Map<string, { orderedQty: number; purchases: number }>> {
  const out = new Map<string, { orderedQty: number; purchases: number }>();
  const db = createBrowserSupabase("admin");
  if (!db) return out;
  const { data, error } = await db.rpc("admin_bundles_overview");
  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  for (const r of (data as any[]) ?? []) {
    if (!r?.bundle_id) continue;
    out.set(String(r.bundle_id), {
      orderedQty: Number(r.ordered_qty) || 0,
      purchases: Number(r.purchases) || 0
    });
  }
  return out;
}

export type BundleAuditEntry = {
  id: string;
  action: "create" | "update" | "status" | "delete";
  actorName: string | null;
  changes: Record<string, unknown>;
  createdAt: string;
};

export async function fetchBundleAuditLog(bundleId: string): Promise<BundleAuditEntry[]> {
  const db = createBrowserSupabase("admin");
  if (!db) return [];
  const { data, error } = await db
    .from("bundle_audit_log")
    .select("id, action, actor_name, changes, created_at")
    .eq("bundle_id", bundleId)
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw new Error(error.message);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data as any[]) ?? []).map((r) => ({
    id: String(r.id),
    action: r.action,
    actorName: r.actor_name ?? null,
    changes: (r.changes && typeof r.changes === "object" ? r.changes : {}) as Record<string, unknown>,
    createdAt: String(r.created_at ?? "")
  }));
}
